<template>
  <el-dialog
    :title="text.transferTitle"
    :visible="visible"
    width="560px"
    @update:visible="$emit('update:visible', $event)">
    <!-- 409 문구에 **소속 매장 수**가 들어 있다(§3-3) — 왜 목적지를 골라야 하는지 서버가 이미 말했다 -->
    <el-alert v-if="conflictMessage" :title="conflictMessage" type="warning" :closable="false" show-icon />

    <el-form label-width="90px" size="small" class="store-transfer__form">
      <el-form-item :label="text.agencyLabel">
        <span>{{ agencyLabel }}</span>
      </el-form-item>
      <el-form-item :label="text.transferDestLabel">
        <!-- 옮길 곳에 **자기 자신**은 없다 — 고르는 순간 아무것도 옮기지 않은 채 거절된다 -->
        <TransferDestinationPicker
          v-model="destination"
          :operators="operators"
          :current="currentMapping"
          :exclude-agency-cd="agency && agency.agencyCd"
          :reset-seq="resetSeq" />
      </el-form-item>
    </el-form>

    <p v-if="serverMessage" class="store-transfer__error">{{ serverMessage }}</p>

    <span slot="footer">
      <el-button size="small" @click="$emit('update:visible', false)">{{ text.cancelAction }}</el-button>
      <el-button size="small" type="primary" :loading="saving" :disabled="!destination" @click="save">{{
        text.transferAction
      }}</el-button>
    </span>
  </el-dialog>
</template>

<script>
import TransferDestinationPicker from '@/components/callpop/TransferDestinationPicker.vue'
import { httpErrorMessage } from '@/plugins/http/policy'
import { CALLPOP_FORM_WRITE_CONFIG, agencyUrl, deactivateBody, orgLabel } from '@/utils/callpopOrg'
import { CALLPOP_TEXT } from '@/utils/callpopText'

/**
 * 관리점 비활성 — **소속 매장이 있으면 옮길 곳을 먼저 정한다**(§3-3: 목적지 없이 부르면 409).
 *
 * 화면 흐름은 서버의 두 단계를 그대로 따른다: 목적지 없이 한 번 부르고(409), 그 문구를 보이며 이
 * 다이얼로그를 열고, 고른 목적지로 다시 부른다. 「소속 매장이 있는가」를 화면이 먼저 세지 않는 이유는
 * 그 판정이 서버의 것이고 화면이 세면 두 벌이 되기 때문이다 — 목록의 `storeCount` 는 조회 시점의 값이다.
 *
 * 목적지 선택기는 매장 매핑과 **같은 컴포넌트**다(브리프 §3).
 */
export default {
  name: 'StoreDeactivateTransferDialog',

  components: { TransferDestinationPicker },

  props: {
    visible: { type: Boolean, default: false },
    /** 비활성할 관리점 행 */
    agency: { type: Object, default: null },
    operators: { type: Array, default: () => [] },
    /** 목적지 없이 부른 첫 시도의 409 문구 */
    conflictMessage: { type: String, default: '' },
  },

  data() {
    return { text: CALLPOP_TEXT, destination: null, serverMessage: '', saving: false, resetSeq: 0 }
  },

  computed: {
    agencyLabel() {
      return orgLabel(this.agency && this.agency.agencyNm, this.agency && this.agency.code)
    },
    /** 이 관리점 자신이 현재 위치다 — 자기에게로 옮기는 칸을 만들지 않는다 */
    currentMapping() {
      const row = this.agency || {}

      return { operator: row.operator || null, agency: { agencyCd: row.agencyCd } }
    },
  },

  watch: {
    visible(open) {
      if (!open) return
      this.destination = null
      this.serverMessage = ''
      this.resetSeq += 1
    },
  },

  methods: {
    async save() {
      if (!this.agency || !this.destination) return

      this.serverMessage = ''
      this.saving = true

      try {
        const response = await this.$axios.put(
          agencyUrl(this.agency.agencyCd, 'deactivate'),
          deactivateBody(this.destination),
          CALLPOP_FORM_WRITE_CONFIG
        )

        this.$emit('saved', response.data)
        this.$emit('update:visible', false)
      } catch (error) {
        this.serverMessage = httpErrorMessage(error, this.text.loadFailed)
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.store-transfer__form {
  margin-top: 12px;
}
.store-transfer__error {
  color: #f56c6c;
  font-size: 12px;
  margin: 0 0 4px;
}
</style>
